import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { ExpandedTooltip, Icon } from '@livable/saas-react-components';
import { Button } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { formatUtilityLabel, getExtractedTotalAmount } from 'src/pages/NewBillingModule/ApplyBillingFull/BillAutomationExtraction';
import { APPFOLIO_PUSH_STATUS, AppfolioPushMessages } from 'src/pages/NewBillingModule/ApplyBillingFull/AppfolioPush';
import { getAppfolioPushChipProps, getAppfolioPushMenuHint } from '../appfolioPushRow';
import useStyles from './billDetailsStyles';
import { DASH, formatDate, formatMoney, getCommonServiceDates, getServicePeriods, pickValue } from './billDetailsUtils';
import { normalizeAmounts } from './billEditUtils';
import { BillSummaryEditTable } from './BillSummaryEditTable';
import { getServiceIndexes, getServiceTypes, matchBillUtilities, selectServices } from './billUtilityMatching';
import { ConfirmActionModal } from './ConfirmActionModal';
import { ExtraUtilitiesCard } from './ExtraUtilitiesCard';
import { PropertyLineItems } from './PropertyLineItems';

/** What the action does, in its own terms — not as an exception to allocating. */
const PUSH_HINT = 'Send this bill to AppFolio as an Accounts Payable record. This does not affect resident charges.';

/**
 * One document is one invoice and one payable, whatever it covers — so the confirmation is about the
 * whole bill, and it says the parts that cannot be taken back from here.
 */
const PUSH_CONFIRM_MESSAGE = 'This creates one Accounts Payable bill in AppFolio for the whole invoice, for the amount the ' + 'document states. It cannot be undone from Livable, and the bill cannot be pushed again. Check the ' + 'bill details and utility mappings before continuing.';

/** The Livable property each block of the bill was matched to, for reading its GL account back. */
const getPropertyLivableId = (data, propertyIndex) => {
  var _data$properties;
  return (data === null || data === void 0 || (_data$properties = data.properties) === null || _data$properties === void 0 || (_data$properties = _data$properties[propertyIndex]) === null || _data$properties === void 0 ? void 0 : _data$properties.propertyId) || '';
};

/**
 * Extracted bill summary and per-property line items (Figma: "Bill details" card), covering only the
 * utilities the matched property is set up for. Editing works on a draft that recalculates as it is
 * typed in; saving hands it to `onSave`. Whatever else the bill charges for follows in its own card.
 */
export function BillDetailsCard(_ref) {
  var _view$finalValidation, _view$finalValidation2;
  let apGlAccounts = _ref.apGlAccounts,
    billPropertyIds = _ref.billPropertyIds,
    customerName = _ref.customerName,
    data = _ref.data,
    _ref$editBlockedReaso = _ref.editBlockedReason,
    editBlockedReason = _ref$editBlockedReaso === void 0 ? null : _ref$editBlockedReaso,
    _ref$isAppfolio = _ref.isAppfolio,
    isAppfolio = _ref$isAppfolio === void 0 ? false : _ref$isAppfolio,
    _ref$isInvoiceNumberL = _ref.isInvoiceNumberLocked,
    isInvoiceNumberLocked = _ref$isInvoiceNumberL === void 0 ? false : _ref$isInvoiceNumberL,
    onPush = _ref.onPush,
    onSave = _ref.onSave,
    properties = _ref.properties,
    _ref$pushRefusalCode = _ref.pushRefusalCode,
    pushRefusalCode = _ref$pushRefusalCode === void 0 ? null : _ref$pushRefusalCode,
    _ref$pushState = _ref.pushState,
    pushState = _ref$pushState === void 0 ? null : _ref$pushState,
    _ref$showPush = _ref.showPush,
    showPush = _ref$showPush === void 0 ? false : _ref$showPush;
  const classes = useStyles();
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    draft = _useState2[0],
    setDraft = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isPushConfirmOpen = _useState4[0],
    setIsPushConfirmOpen = _useState4[1];
  const isEditing = draft !== null;
  const view = isEditing ? draft : data;
  const _useMemo = useMemo(() => {
      const groups = matchBillUtilities({
        billPropertyIds,
        data: view,
        properties
      });
      return {
        covered: selectServices(groups, true),
        extra: selectServices(groups, false)
      };
    }, [billPropertyIds, properties, view]),
    covered = _useMemo.covered,
    extra = _useMemo.extra;
  const summary = useMemo(() => {
    var _pickValue, _pickValue2;
    const serviceTypes = getServiceTypes(covered);
    const coveredServices = covered.flatMap(group => group.services.map(item => item.service));
    return {
      accountNumber: (_pickValue = pickValue(view, ['accountNumber'])) !== null && _pickValue !== void 0 ? _pickValue : DASH,
      addresses: ((view === null || view === void 0 ? void 0 : view.properties) || []).map(property => property.address).filter(Boolean),
      billDate: formatDate(pickValue(view, ['billDate', 'postingDate'])),
      customFields: ((view === null || view === void 0 ? void 0 : view.customFields) || []).filter(field => field.label || field.value),
      dueDate: formatDate(pickValue(view, ['dueDate', 'billDueDate'])),
      invoiceNumber: (_pickValue2 = pickValue(view, ['invoiceNumber', 'invoiceNo'])) !== null && _pickValue2 !== void 0 ? _pickValue2 : DASH,
      provider: (view === null || view === void 0 ? void 0 : view.vendorName) || DASH,
      serviceDates: getCommonServiceDates(coveredServices, view),
      servicePeriods: getServicePeriods(coveredServices, view),
      serviceTypes,
      totalDue: formatMoney(getExtractedTotalAmount(view), view === null || view === void 0 ? void 0 : view.currency),
      utilities: serviceTypes.length ? serviceTypes.map(formatUtilityLabel).join(', ') : DASH
    };
  }, [covered, view]);
  const isVerified = (view === null || view === void 0 || (_view$finalValidation = view.finalValidation) === null || _view$finalValidation === void 0 ? void 0 : _view$finalValidation.isValid) === true;
  const validationReason = (view === null || view === void 0 || (_view$finalValidation2 = view.finalValidation) === null || _view$finalValidation2 === void 0 ? void 0 : _view$finalValidation2.reason) || (isVerified ? 'Provider, utilities, and address match with existing property records.' : 'Some validation checks did not pass. Review the extracted values before continuing.');
  const handleSave = () => {
    onSave(normalizeAmounts(draft));
    setDraft(null);
  };
  const handleConfirmPush = () => {
    setIsPushConfirmOpen(false);
    onPush();
  };

  /**
   * Before a push: the account a payable for this bill *would* post to. It is the property's default,
   * and a utility configured with its own would override it at push time — but the bill carries no
   * property-utility id to look that up by, so the label says "Default" and means it.
   *
   * After one: the account it *did* post to, read back from what was sent. That is no longer a default
   * — a utility override has already been applied by then — so the label stops calling it one.
   */
  const apGlLabel = apGlAccounts !== null && apGlAccounts !== void 0 && apGlAccounts.isEnabled ? apGlAccounts.getSummaryLabel(covered.map(group => getPropertyLivableId(view, group.propertyIndex)).filter(Boolean)) : null;
  const apGlSummaryLabel = apGlAccounts !== null && apGlAccounts !== void 0 && apGlAccounts.isRecorded ? 'Accounts Payable GL Account (Expense), as pushed:' : 'Default Accounts Payable GL Account (Expense):';
  const pushChip = pushState ? getAppfolioPushChipProps(pushState) : null;
  const isPushed = (pushState === null || pushState === void 0 ? void 0 : pushState.status) === APPFOLIO_PUSH_STATUS.succeeded;
  const pushDisabledHint = pushState ? getAppfolioPushMenuHint(pushState) : null;
  const emptyStateMessage = ((view === null || view === void 0 ? void 0 : view.properties) || []).length ? 'None of the utilities on this bill are set up on the properties it matched.' : 'No property line items were extracted for this bill.';
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.card, " ").concat(isEditing ? classes.cardOverflowing : '')
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cardHeader
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cardHeaderTitles
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.cardTitle
  }, isEditing ? 'Edit bill details' : 'Bill details'), !isEditing && isAppfolio && pushChip ? /*#__PURE__*/React.createElement("span", {
    className: "".concat(classes.statusChip, " ").concat(isPushed ? classes.statusChipDone : '')
  }, pushChip.label) : null), isEditing ? null :
  /*#__PURE__*/
  // The reason hangs on the wrapper: a disabled button hears no pointer events of its own, so
  // a title on it would never be read out.
  React.createElement(ExpandedTooltip, {
    placement: "top",
    title: editBlockedReason !== null && editBlockedReason !== void 0 ? editBlockedReason : '',
    type: "grey"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("button", {
    "aria-label": "Edit bill details",
    className: classes.iconButton,
    disabled: !onSave || !!editBlockedReason,
    onClick: () => setDraft(data),
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "edit"
  }))))), isEditing ? /*#__PURE__*/React.createElement(BillSummaryEditTable, {
    customerName: customerName,
    isInvoiceNumberLocked: isInvoiceNumberLocked,
    onChange: setDraft,
    serviceDates: summary.serviceDates,
    serviceTypes: summary.serviceTypes,
    value: draft
  }) : /*#__PURE__*/React.createElement("div", {
    className: classes.summary
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.matchBox, " ").concat(isVerified ? '' : classes.matchBoxWarning)
  }, isVerified ? /*#__PURE__*/React.createElement(Icon, {
    type: "check-circle"
  }) : /*#__PURE__*/React.createElement(Icon, {
    state: "warning",
    type: "warning"
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.matchTitle, " ").concat(isVerified ? '' : classes.matchTitleWarning)
  }, isVerified ? 'Match: Verified' : 'Match: Review recommended'), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.matchText, " ").concat(isVerified ? '' : classes.matchTextWarning)
  }, validationReason))), /*#__PURE__*/React.createElement("div", {
    className: classes.sectionLabel
  }, "Summary:"), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Customer:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, (view === null || view === void 0 ? void 0 : view.customerName) || customerName || DASH)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Provider:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.provider)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Utilities:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.utilities)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Properties:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.addresses.length ? summary.addresses.map(address => /*#__PURE__*/React.createElement("div", {
    key: address
  }, address)) : DASH))), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Account #:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.accountNumber)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Invoice #:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.invoiceNumber)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Bill date:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.billDate)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Due date:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.dueDate)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Service dates:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.servicePeriods.length ? summary.servicePeriods.map(entry => /*#__PURE__*/React.createElement("div", {
    key: entry.period
  }, summary.servicePeriods.length > 1 ? "".concat(entry.utilities, ": ").concat(entry.period) : entry.period)) : DASH))), apGlLabel ? /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.summaryRow, " ").concat(classes.summaryRowStacked)
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabelStacked
  }, apGlSummaryLabel), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, apGlLabel))) : null, summary.customFields.length ? /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, summary.customFields.map((field, index) => /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow,
    key: "".concat(field.label, "-").concat(index)
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabelWide
  }, field.label || DASH, ":"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, field.value || DASH)))) : null, /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Total due:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.totalDue))), /*#__PURE__*/React.createElement("div", {
    className: classes.sectionLabel
  }, "Line items by property:")), isEditing ? /*#__PURE__*/React.createElement("div", {
    className: classes.editSectionRow
  }, "Line items per property:") : null, covered.length ? covered.map(group => /*#__PURE__*/React.createElement(PropertyLineItems, {
    currency: view === null || view === void 0 ? void 0 : view.currency,
    data: view
    // Its own property's account, not the bill's: two properties on one bill can post to
    // different ones, and a line posts where its property says. Once pushed, this is the
    // account that property's lines actually posted to.
    ,
    glAccountLabel: apGlAccounts !== null && apGlAccounts !== void 0 && apGlAccounts.isEnabled ? apGlAccounts.getPropertyLabel(getPropertyLivableId(view, group.propertyIndex)) : null,
    isEditing: isEditing,
    key: group.propertyIndex,
    onChange: setDraft,
    propertyIndex: group.propertyIndex,
    serviceIndexes: getServiceIndexes(group),
    showGlCode: !!(apGlAccounts !== null && apGlAccounts !== void 0 && apGlAccounts.isEnabled)
  })) : /*#__PURE__*/React.createElement("div", {
    className: classes.emptyState
  }, emptyStateMessage), isEditing ? /*#__PURE__*/React.createElement("div", {
    className: classes.editFooter
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => setDraft(null),
    size: "small",
    variant: "outlined"
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    onClick: handleSave,
    size: "small",
    variant: "contained"
  }, "Save")) : null, !isEditing && isAppfolio && pushState ? /*#__PURE__*/React.createElement("div", {
    className: classes.cardActions
  }, /*#__PURE__*/React.createElement(AppfolioPushMessages, {
    blockers: pushState.blockers,
    className: classes.cardActionsMessages,
    disabledReason: null,
    liveAttempt: pushState.liveAttempt,
    pushStatus: pushState.pushStatus,
    refusalCode: pushRefusalCode
  }), showPush ?
  /*#__PURE__*/
  // A disabled button hears no hover, so the reason it is disabled hangs on the wrapper.
  React.createElement(ExpandedTooltip, {
    placement: "top",
    title: pushDisabledHint || PUSH_HINT,
    type: "grey"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button, {
    disabled: !onPush || !pushState.canPush,
    onClick: () => setIsPushConfirmOpen(true),
    size: "small",
    variant: "contained"
  }, isPushed ? 'Pushed to AppFolio' : 'Push to AppFolio'))) : null, /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: "Push to AppFolio",
    isOpen: isPushConfirmOpen,
    message: PUSH_CONFIRM_MESSAGE,
    onClose: () => setIsPushConfirmOpen(false),
    onConfirm: handleConfirmPush,
    title: "Push bill to AppFolio?"
  })) : null), extra.length ? /*#__PURE__*/React.createElement(ExtraUtilitiesCard, {
    currency: view === null || view === void 0 ? void 0 : view.currency,
    data: view,
    groups: extra
  }) : null);
}